import { computed, reactive, readonly } from "vue";
import { api, type Me, type Membership } from "@/api";

/**
 * Authentication state for the portal.
 *
 * A plain reactive object rather than Pinia: there is one store, it has no
 * cross-store dependencies, and adding a state library for it would be
 * ceremony. Introduce Pinia when a second store genuinely needs to exist.
 */
interface SessionState {
  ready: boolean;
  authenticated: boolean;
  userId: string | null;
  email: string | null;
  displayName: string | null;
  memberships: Membership[];
  activeOrgId: string | null;
}

const state = reactive<SessionState>({
  ready: false,
  authenticated: false,
  userId: null,
  email: null,
  displayName: null,
  memberships: [],
  activeOrgId: null,
});

const ACTIVE_ORG_KEY = "sudo.activeOrg";

function apply(me: Me) {
  state.authenticated = me.authenticated;
  state.userId = me.userId;
  state.email = me.email;
  state.displayName = me.displayName;
  state.memberships = me.memberships ?? [];

  const remembered = readStoredOrg();
  const known = state.memberships.some((membership) => membership.orgId === remembered);

  state.activeOrgId = known ? remembered : (state.memberships[0]?.orgId ?? null);
}

function clear() {
  state.authenticated = false;
  state.userId = null;
  state.email = null;
  state.displayName = null;
  state.memberships = [];
  state.activeOrgId = null;
}

function readStoredOrg(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

export const session = {
  state: readonly(state),

  activeOrg: computed(() =>
    state.memberships.find((membership) => membership.orgId === state.activeOrgId) ?? null),

  /**
   * Resolves the session before the app renders.
   *
   * Called once at bootstrap so the router never has to guess. Without this the
   * first navigation happens against unknown state and an authenticated user
   * gets flashed the login screen on every refresh.
   */
  async load(): Promise<void> {
    try {
      apply(await api.me());
    } catch {
      // An unreachable API is indistinguishable from being logged out as far as
      // routing is concerned; both mean "do not show protected content".
      clear();
    } finally {
      state.ready = true;
    }
  },

  async login(email: string, password: string): Promise<void> {
    apply(await api.login(email, password));
  },

  async register(
    email: string,
    password: string,
    displayName: string,
    organisationName: string,
  ): Promise<void> {
    apply(await api.register(email, password, displayName, organisationName));
  },

  async logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the call failed. Leaving the UI looking signed in
      // after the user asked to leave is worse than a stale server session,
      // which expires on its own anyway.
      clear();
    }
  },

  setActiveOrg(orgId: string) {
    state.activeOrgId = orgId;
    try {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    } catch {
      // Private browsing blocks writes; the choice still applies this session.
    }
  },

  /** Permission check for the active organisation. Cosmetic only. */
  can(permission: string): boolean {
    const membership = state.memberships.find((item) => item.orgId === state.activeOrgId);
    return membership?.permissions.includes(permission) ?? false;
  },
};
